import React, { Component } from 'react';
import { Link } from "react-router-dom"
import { Col, Row, DatePicker } from 'antd';
import { Button, Spin, Table, Tag, Space } from 'antd';
import { Typography } from 'antd';
import { CalendarOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios'

const { RangePicker } = DatePicker;
const { Title } = Typography;

const DOMAINS = [
  ['BS', '기초생명의학 지식', '#13c2c2'],
  ['CD', '임상적 의사결정 능력', '#f5222d'],
  ['CSIA', '개별 동물에서의 건강과 질병 관리를 위한 임상 기술', '#5468ff'],
  ['MAH', '군집동물의 건강과 질병 관리', '#52c41a'],
  ['OHPH', '원헬스와 공중보건 전문성', '#722ed1'],
  ['SC', '학문과 연구', '#faad14'],
  ['VP', '수의전문직업성', '#a0d911'],
  ['CAC', '소통과 협력', '#37e4e4'],
];

const DOMAIN_COLOR_MAP = {};
DOMAINS.forEach(([code, , color]) => { DOMAIN_COLOR_MAP[code] = color; });

const LIST_TYPE_TITLES = {
  'waiting': '승인대기 실습 목록',
  'rejected': '반려된 실습 목록',
  'done': '승인완료 실습 목록',
};

export default class ProfList extends Component {
  state = {
    data: [],
    loading: true,
    filteredInfo: null,
    sortedInfo: null,
    dateRange: null,
  }

  componentDidMount() {
    const reqData = { list_type: this.props.match.params.state };
    axios('/record/prof_list', {
      method: "POST",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      data: reqData
    }).then(res => {
      const { waiting_records, usernames, professors } = res.data;
      const data = waiting_records.map((record, i) => ({
        key: record.id,
        primary_class: record.primary_class,
        start_at: record.start_at,
        end_at: record.end_at,
        institute: record.institute,
        content: record.practice,
        tags: record.competency ? record.competency.split(' ') : [],
        student: usernames[i],
        professor: professors ? professors[i] : '',
        state: record.state,
        id: record.id,
      }));
      this.setState({ data, loading: false });
    });
  }

  handleChange = (pagination, filters, sorter) => {
    this.setState({ filteredInfo: filters, sortedInfo: sorter });
  };

  render() {
    if (this.state.loading) {
      return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><Spin size="large" tip="로딩 중..." /></div>;
    }

    let { sortedInfo, filteredInfo } = this.state;
    sortedInfo = sortedInfo || {};
    filteredInfo = filteredInfo || {};

    const columns = [
      {
        title: '실습 분류',
        dataIndex: 'primary_class',
        key: 'primary_class',
        sorter: (a, b) => (a.primary_class || '').localeCompare(b.primary_class || ''),
        sortOrder: sortedInfo.columnKey === 'primary_class' && sortedInfo.order,
        filters: [...new Set(this.state.data.map(d => d.primary_class).filter(Boolean))].sort()
          .map(v => ({ text: v, value: v })),
        filteredValue: filteredInfo.primary_class || null,
        onFilter: (value, record) => record.primary_class === value,
      },
      {
        title: '실습 날짜',
        dataIndex: 'id',
        key: 'date',
        render: (id, record) => (
          <Link to={`/review/${id}`}>
            {new Date(record.start_at).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
            {' ~ '}
            {new Date(record.end_at).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Link>
        ),
        sorter: (a, b) => new Date(a.start_at) - new Date(b.start_at),
        sortOrder: sortedInfo.columnKey === 'date' && sortedInfo.order,
        filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
          <div style={{ padding: 8 }}>
            <RangePicker
              value={this.state.dateRange}
              onChange={(dates) => {
                this.setState({ dateRange: dates });
                setSelectedKeys(dates ? [dates] : []);
              }}
              style={{ marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => confirm()}>검색</Button>
              <Button size="small" onClick={() => { this.setState({ dateRange: null }); clearFilters(); confirm(); }}>초기화</Button>
            </Space>
          </div>
        ),
        filterIcon: () => <CalendarOutlined style={{ color: this.state.dateRange ? '#1890ff' : undefined }} />,
      },
      {
        title: '실습 기관',
        dataIndex: 'institute',
        key: 'institute',
        sorter: (a, b) => (a.institute || '').localeCompare(b.institute || ''),
        sortOrder: sortedInfo.columnKey === 'institute' && sortedInfo.order,
      },
      {
        title: '실습 개요',
        dataIndex: 'content',
        key: 'content',
      },
      {
        title: '졸업 역량',
        key: 'tags',
        dataIndex: 'tags',
        filters: DOMAINS.map(([code, name, color]) => ({
          text: <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: color, marginRight: 6 }} />{code} - {name}</span>,
          value: code,
        })),
        filteredValue: filteredInfo.tags || null,
        render: tags => (
          <>
            {tags.map(tag => {
              const color = DOMAIN_COLOR_MAP[tag] || (tag.length > 5 ? 'geekblue' : 'green');
              return <Tag color={color} key={tag}>{tag.toUpperCase()}</Tag>;
            })}
          </>
        ),
      },
      {
        title: '학생명',
        key: 'student',
        dataIndex: 'student',
        filters: [...new Set(this.state.data.map(d => d.student).filter(Boolean))].sort()
          .map(v => ({ text: v, value: v })),
        filteredValue: filteredInfo.student || null,
        onFilter: (value, record) => record.student === value,
      },
      {
        title: '지도교수',
        key: 'professor',
        dataIndex: 'professor',
        filters: [...new Set(this.state.data.map(d => d.professor).filter(Boolean))].sort()
          .map(v => ({ text: v, value: v })),
        filteredValue: filteredInfo.professor || null,
        onFilter: (value, record) => record.professor === value,
      },
    ];

    const listType = this.props.match.params.state;
    const title = LIST_TYPE_TITLES[listType] || '실습 목록';

    const filteredData = this.state.data.filter(record => {
      if (filteredInfo.tags && filteredInfo.tags.length > 0) {
        if (!filteredInfo.tags.every(tag => record.tags.includes(tag))) return false;
      }
      if (this.state.dateRange && this.state.dateRange[0] && this.state.dateRange[1]) {
        const start = new Date(record.start_at);
        const end = new Date(record.end_at);
        const rangeStart = this.state.dateRange[0].startOf('day').toDate();
        const rangeEnd = this.state.dateRange[1].endOf('day').toDate();
        if (end < rangeStart || start > rangeEnd) return false;
      }
      return true;
    });

    return (
      <Col xs={{ span: 22, offset: 1 }} sm={{ span: 20, offset: 2 }} md={{ span: 20, offset: 2 }} lg={{ span: 20, offset: 2 }} xxl={{ span: 16, offset: 4 }} style={{ marginTop: '40px' }}>
        <Row span={12}>
          <Col><Title level={3}>{title}</Title></Col>
        </Row>
        <Row span={12}>
          <Col span={24}>
            <Table columns={columns} dataSource={filteredData} onChange={this.handleChange} />
          </Col>
        </Row>
      </Col>
    );
  }
}
